/**
 * Public Talking profile (`/u/:id`). Listeners do not review talkers in public.
 * How did it go? stays private / ops-only.
 */

import { REVIEW_TYPE_OF_CUSTOMER } from './types';
import { normalizeTopics, normalizeTopicsOther } from './sessionTopics';

/**
 * True when this review is a listener writing about a talker.
 * Those must never appear on a public surface.
 *
 * @param {Object} [review]
 * @returns {boolean}
 */
export const isListenerReviewOfTalker = review =>
  review?.attributes?.type === REVIEW_TYPE_OF_CUSTOMER;

/**
 * Drop listener→talker reviews from a public list.
 *
 * @param {Array} [reviews]
 * @returns {Array}
 */
export const publicTalkerSafeReviews = (reviews = []) =>
  (Array.isArray(reviews) ? reviews : []).filter(review => !isListenerReviewOfTalker(review));

/**
 * Topics the talker chose on their Talking profile.
 *
 * @param {Object} [user]
 * @returns {{ topics: string[], topicsOther: string }}
 */
export const talkerProfileTopics = user => {
  const publicData = user?.attributes?.profile?.publicData || {};
  return {
    topics: normalizeTopics(publicData.interestedTopics),
    topicsOther: normalizeTopicsOther(publicData.interestedTopicsOther),
  };
};

/**
 * Account createdAt for “Member since”.
 *
 * @param {Object} [user]
 * @returns {Date|null}
 */
export const talkerMemberSince = user => {
  const raw = user?.attributes?.createdAt;
  if (!raw) {
    return null;
  }
  const date = raw instanceof Date ? raw : new Date(raw);
  return Number.isFinite(date.getTime()) ? date : null;
};

/**
 * Completed talk-session count when the profile already stores one.
 *
 * @param {Object} [user]
 * @returns {number|null}
 */
export const talkerSessionsCompleted = user => {
  const profile = user?.attributes?.profile || {};
  const raw =
    profile.metadata?.talkSessionsCompleted ??
    profile.publicData?.talkSessionsCompleted ??
    profile.metadata?.sessionsCompleted ??
    profile.publicData?.sessionsCompleted;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
};
